import { lazy } from 'react';
import { onValue, ref } from 'firebase/database';
import { rtdb } from './firebase/services';

// ─── Lazy loading ──────────────────────────────────────────────────────────

const RELOAD_FLAG = 'celebrations.chunkReloadedAt';

/**
 * React.lazy for modules that only provide named exports. If a chunk fails to load
 * (typically a stale tab after a new deploy), reload once to pick up the new build.
 */
export function lazyNamed(loader, exportName) {
  return lazy(() => loader().then(
    module => ({ default: module[exportName] }),
    error => {
      const lastReload = Number(window.sessionStorage.getItem(RELOAD_FLAG)) || 0;
      if (Date.now() - lastReload > 60_000) {
        window.sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
        window.location.reload();
        return new Promise(() => {});
      }
      throw error;
    }
  ));
}

// ─── Server clock ──────────────────────────────────────────────────────────

let offsetMs = 0;
let listening = false;

function listenForOffset() {
  if (listening) return;
  listening = true;
  try {
    onValue(ref(rtdb, '.info/serverTimeOffset'), snapshot => {
      const value = Number(snapshot.val());
      offsetMs = Number.isFinite(value) ? value : 0;
    });
  } catch (error) {
    console.warn('Server clock offset unavailable; using the local clock.', error);
  }
}

/** Current time aligned to the Firebase server clock, so game timers agree across devices. */
export function serverNow() {
  listenForOffset();
  return Date.now() + offsetMs;
}

// ─── Ranking and game history ──────────────────────────────────────────────

/** Competition ranking (1, 1, 3): players with equal scores share a rank. */
export function rankPlayersByScore(playerIds, scores = {}) {
  const sorted = [...playerIds].sort((a, b) => (scores[b] || 0) - (scores[a] || 0));
  const ranks = {};
  sorted.forEach((uid, index) => {
    const previous = sorted[index - 1];
    ranks[uid] = index > 0 && (scores[uid] || 0) === (scores[previous] || 0)
      ? ranks[previous]
      : index + 1;
  });
  return { sorted, ranks };
}

// Builds the history record for a finished game (pure, no I/O).
// `order` lists every participant (humans and bots) best-first; bots count as competitors,
// so a win means finishing first overall. `ranks` may override index-based ranks (ties).
export function buildPlacementHistoryPayload({ gameType, roomId, userId, room, order = [], ranks, scores }) {
  const isBot = uid => Boolean(room.players?.[uid]?.isBot);
  const nameOf = uid => room.players?.[uid]?.name || uid;
  const rankOf = uid => ranks?.[uid] ?? order.indexOf(uid) + 1;
  const humans = order.filter(uid => !isBot(uid));

  return {
    gameType,
    roomId,
    isWinner: rankOf(userId) === 1,
    myRank: rankOf(userId) || order.length,
    totalPlayers: order.length,
    botsPlayed: order.length - humans.length,
    winnerName: order.filter(uid => rankOf(uid) === 1).map(nameOf).join(', '),
    opponents: humans.filter(uid => uid !== userId).map(nameOf),
    rankedPlayers: order.map(uid => ({
      name: nameOf(uid),
      score: scores ? (scores[uid] || 0) : null,
      rank: rankOf(uid),
      isMe: uid === userId,
      isBot: isBot(uid),
    })),
  };
}

// Score-based games: higher score ranks first, equal scores share a rank.
export function buildGameHistoryPayload({ gameType, roomId, userId, room, playerOrder = [], scores = {} }) {
  const { ranks, sorted } = rankPlayersByScore(playerOrder, scores);
  return buildPlacementHistoryPayload({ gameType, roomId, userId, room, order: sorted, ranks, scores });
}

export const isHumanParticipant = (room, playerOrder, userId) =>
  (playerOrder || []).some(uid => uid === userId && !room.players?.[uid]?.isBot);

// Reads rank and win state from a stored record; older records only have a human-only myRank.
export function getPlacement(game) {
  const rank = game.myRank || game.rank || 0;
  const isWin = typeof game.isWinner === 'boolean' ? game.isWinner : rank === 1;
  return { rank, isWin, isTop3: isWin || (rank > 0 && rank <= 3) };
}
